import PendingStockOperationNotice from "../components/stock/PendingStockOperationNotice";
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Calendar, Views, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { format, parse, startOfWeek, getDay, startOfDay, addDays } from 'date-fns';
import { enUS, pl as plLocale } from 'date-fns/locale';
import { fromZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import './CalendarTweaks.css'; // ← your visual polish (sticky headers, gutter, zebra, etc.)

import { fetchEmployees } from '../services/employeeService';
import { fetchCalendarEvents, updateVisitFromCalendar } from '../services/visitCalendarService';

// ----- Config -----
const WARSAW_TZ = 'Europe/Warsaw';
const locales = { 'en-US': enUS, pl: plLocale };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// All calendar label formats in 24-hour style
const formats = {
  dayFormat: (date, culture, loc) => loc.format(date, 'EEEE, d MMMM yyyy'),
  timeGutterFormat: 'H:mm',
  eventTimeRangeFormat: ({ start, end }, culture, loc) =>
    `${loc.format(start, 'H:mm')}–${loc.format(end, 'H:mm')}`,
  agendaTimeRangeFormat: ({ start, end }, culture, loc) =>
    `${loc.format(start, 'H:mm')}–${loc.format(end, 'H:mm')}`,
};

// ----- Helpers -----
// Difference in minutes between 2 Date objects (non-negative)
function diffMinutes(start, end) {
  return Math.max(0, Math.round((end - start) / 60000));
}
// Convert minutes to hours + minutes object
function minutesToHM(mins) {
  const H = Math.floor(mins / 60);
  const M = mins % 60;
  return { H, M };
}
// Preset durations for quick picks
const DURATION_PRESETS = [
  { H: 0, M: 15 }, { H: 0, M: 30 }, { H: 0, M: 45 },
  { H: 1, M: 0 },  { H: 1, M: 15 }, { H: 1, M: 30 }, { H: 1, M: 45 },
  { H: 2, M: 0 }
];

/**
 * What is rendered inside each event box.
 * Single line: [StatusBadge] 123.45 zł • Service • Client
 */
function EventContent({ event }) {
  const { t } = useTranslation('visits');
  // The backend currently stores visit status values as Polish domain codes.
  // Keep those codes untouched for comparisons and updates, and translate only the displayed label.
  const statusCode = event?.status === 'Wykonane' ? 'Wykonane' : 'Niewykonane';
  const statusLabel = statusCode === 'Wykonane'
    ? t('status.performed')
    : t('status.notPerformed');
  const rawValue = event?.extendedProps?.value;
  const parsedValue = (typeof rawValue === 'string')
    ? Number(rawValue.replace(',', '.'))
    : Number(rawValue);

  const client = event?.extendedProps?.client;
  const service = event?.extendedProps?.service;

  const trailing = [
    service ? String(service) : null,
    client ? t('calendarDay.event.clientTrail', { client }) : null,
  ].filter(Boolean).join(' • ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        lineHeight: 1.2,
        fontSize: '0.85rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title="" // prevent native browser tooltip
    >
      {/* Status badge */}
      <span
        style={{
          fontSize: '0.72rem',
          padding: '2px 6px',
          borderRadius: 6,
          color: '#fff',
          backgroundColor: statusCode === 'Wykonane' ? '#198754' : '#6c757d',
          flex: '0 0 auto',
        }}
      >
        {statusLabel}
      </span>

      {/* Monetary value, if valid */}
      {Number.isFinite(parsedValue) && (
        <strong style={{ fontSize: '0.9rem', flex: '0 0 auto' }}>
          {parsedValue.toFixed(2)} {t('calendarDay.currency.plnShort')}
        </strong>
      )}

      {/* Service • Client (ellipsis if overflow) */}
      <span
        style={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: '1 1 auto',
        }}
      >
        {trailing}
      </span>
    </div>
  );
}

/**
 * Resource (employee) header for each column.
 * Shows a small initials "avatar" + employee name to improve recognition.
 */
function ResourceHeader({ label /* = employee title */, resource }) {
  const initials = (label || '')
    .split(' ')
    .filter(Boolean)
    .map(s => s[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
      <div
        aria-hidden
        style={{
          width: 24,
          height: 24,
          borderRadius: '9999px',
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: '#0f172a',
          background: '#e2e8f0',
        }}
      >
        {initials || '•'}
      </div>
      <div style={{ fontWeight: 600 }}>{label}</div>
    </div>
  );
}

const RBCResourceDay = () => {
  const { t, i18n } = useTranslation(['visits', 'common']);

  // The calendar itself is still rendered for the Warsaw business day, but its
  // visible labels, date names and form texts follow the current i18n language.
  // The application currently supports Polish and English, so all non-Polish
  // UI languages fall back to English for React Big Calendar/date-fns culture.
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pl')
    ? 'pl'
    : 'en';
  const calendarCulture = currentLanguage === 'pl' ? 'pl' : 'en-US';
  const dateFnsLocale = currentLanguage === 'pl' ? plLocale : enUS;
  const [currentDate, setCurrentDate] = useState(() => startOfDay(new Date()));
  const [employees, setEmployees] = useState([]);   // [{id, title}]
  const [events, setEvents] = useState([]);         // RBC event shape
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Scroll management
  const containerRef = useRef(null);     // scrollable area (the page container)
  const editCardRef  = useRef(null);     // the edit panel/card
  const prevScrollTopRef = useRef(0);    // remembers previous scroll position

  // Edit modal state
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: '',               // "YYYY-MM-DD"
    time: '',               // "HH:mm"
    durationHours: 1,
    durationMinutes: 0,
    employee_id: '',
    status: '',
    status_comment: '',
    service_value: ''
  });

  // Visible UTC range for the Warsaw local day
  const utcRange = useMemo(() => {
    const warsawStart = startOfDay(currentDate);
    const warsawEnd = addDays(warsawStart, 1);
    return {
      fromUTC: fromZonedTime(warsawStart, WARSAW_TZ).toISOString(),
      toUTC:   fromZonedTime(warsawEnd,   WARSAW_TZ).toISOString(),
    };
  }, [currentDate]);

  // Load employees once
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchEmployees();
        setEmployees(rows || []);
      } catch (e) {
        setEmployees([]);
        console.error('[RBC] employees load failed', e);
      }
    })();
  }, []);

  // Load events for the selected day
  const loadEvents = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const apiEvents = await fetchCalendarEvents({
        fromUTC: utcRange.fromUTC,
        toUTC: utcRange.toUTC
      });
      const mapped = (apiEvents || []).map(e => ({
        id: e.id,
        title: e.title || '',
        start: new Date(e.start), // UTC -> Date (RBC renders in local time)
        end: new Date(e.end),
        resourceId: e.assigneeId ?? null,
        status: e.status, // "Wykonane" | "Niewykonane"
        extendedProps: e.extendedProps
      }));
      setEvents(mapped);
    } catch (e) {
      console.error('[RBC] events load failed', e);
      setErrorMsg(t('calendarDay.messages.loadEventsFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, utcRange.fromUTC, utcRange.toUTC]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Remove any native title attributes in the calendar after render (defensive)
  useEffect(() => {
    const root = document.querySelector('.rbc-calendar');
    if (!root) return;
    root.querySelectorAll('[title]').forEach(n => n.removeAttribute('title'));
  }, [events]);

  // When the edit card appears, ensure it's fully visible and focus the first input
  useEffect(() => {
    if (!editing || !containerRef.current || !editCardRef.current) return;

    prevScrollTopRef.current = containerRef.current.scrollTop;

    const cont = containerRef.current;
    const card = editCardRef.current;

    // Re-measure on next frame to be sure the card has final height
    requestAnimationFrame(() => {
      const TOP_PAD = 16;     // leave a little space above
      const BOTTOM_PAD = 160; // extra space below so buttons are fully visible

      const contRect = cont.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      const deltaTop = cardRect.top - contRect.top;
      const deltaBottom = cardRect.bottom - contRect.bottom;

      let targetScrollTop = cont.scrollTop;

      if (deltaTop < TOP_PAD) {
        targetScrollTop += (deltaTop - TOP_PAD);
      }
      if (deltaBottom > BOTTOM_PAD) {
        targetScrollTop += (deltaBottom + BOTTOM_PAD);
      }

      cont.scrollTo({ top: targetScrollTop, behavior: 'smooth' });

      // Focus first interactive control
      const firstInput = card.querySelector('input, select, textarea, button');
      if (firstInput) {
        setTimeout(() => firstInput.focus({ preventScroll: true }), 180);
      }
    });
  }, [editing]);

  // Toolbar handlers
  const goToday = () => setCurrentDate(startOfDay(new Date()));
  const goPrev = () => setCurrentDate(d => addDays(d, -1));
  const goNext = () => setCurrentDate(d => addDays(d, +1));

  // Open edit form when clicking an event
  const onSelectEvent = useCallback((evt) => {
    const start = evt.start;
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    const hh = String(start.getHours()).padStart(2, '0');
    const mm = String(start.getMinutes()).padStart(2, '0');

    const mins = diffMinutes(evt.start, evt.end);
    const { H, M } = minutesToHM(mins);

    setEditing(evt);
    setForm({
      date: `${y}-${m}-${d}`,
      time: `${hh}:${mm}`,
      durationHours: H,
      durationMinutes: M,
      employee_id: evt.resourceId || '',
      status: (evt.status === 'Wykonane') ? 'Wykonane' : 'Niewykonane',
      status_comment: evt.extendedProps?.status_comment || '',
      service_value: evt.extendedProps?.value ?? ''
    });
  }, []);

  // Submit edit
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;

    try {
      const H = Number(form.durationHours) || 0;
      const M = Number(form.durationMinutes) || 0;
      const duration = `${H}:${String(M).padStart(2, '0')}`;

      // Normalize decimal comma in service_value if present
      const normalizedValue =
        form.service_value === '' ? undefined
        : Number(String(form.service_value).replace(',', '.'));

      const payload = {
        date_time_local: `${form.date} ${form.time}`,  // Warsaw wall time
        duration,
        employee_id: form.employee_id ? Number(form.employee_id) : null,
        status: form.status, // "Wykonane" | "Niewykonane"
        status_comment: form.status_comment,
        service_value: normalizedValue
      };

      await updateVisitFromCalendar(editing.id, payload);
      setEditing(null);

      // Restore previous scroll position
      if (containerRef.current) {
        containerRef.current.scrollTo({ top: prevScrollTopRef.current, behavior: 'smooth' });
      }

      await loadEvents();
    } catch (err) {
      console.error('[RBC] update failed', err);
      alert(t('calendarDay.messages.updateVisitFailed'));
    }
  };

  const closeEdit = () => {
    setEditing(null);
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: prevScrollTopRef.current, behavior: 'smooth' });
    }
  };

  // Resource accessors (RBC props)
  const resourceIdAccessor = 'id';
  const resourceTitleAccessor = 'title';

  return (
    <div className="page-container" ref={containerRef}>
      <PendingStockOperationNotice scope="visits" onResolved={loadEvents} />
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h1 className="h3 m-0">{t('calendarDay.title')}</h1>
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-outline-secondary btn-sm" onClick={goPrev}>{t('calendarDay.actions.previous')}</button>
          <button className="btn btn-outline-primary btn-sm" onClick={goToday}>{t('calendarDay.actions.today')}</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={goNext}>{t('calendarDay.actions.next')}</button>
          <strong className="ms-2">
            {format(currentDate, 'EEEE, d MMMM yyyy', { locale: dateFnsLocale })}
          </strong>
        </div>
      </div>

      {/* Small color legend */}
      <div className="d-flex align-items-center gap-3 mt-1 mb-2" aria-label={t('calendarDay.legend.ariaLabel')}>
        <span className="d-inline-flex align-items-center gap-2">
          <span style={{display:'inline-block',width:14,height:14,borderRadius:3,background:'#198754',border:'1px solid #146c43'}} />
          <small>{t('status.performed')}</small>
        </span>
        <span className="d-inline-flex align-items-center gap-2">
          <span style={{display:'inline-block',width:14,height:14,borderRadius:3,background:'#fd7e14',border:'1px solid #e8590c'}} />
          <small>{t('status.notPerformed')}</small>
        </span>
      </div>

      {errorMsg && (
        <div className="alert alert-danger py-2">{errorMsg}</div>
      )}

      <div style={{ height: 'calc(100vh - 230px)' }}>
        <Calendar
          localizer={localizer}
          formats={formats}
          date={currentDate}
          onNavigate={setCurrentDate}
          view={Views.DAY}
          onView={() => {}}
          events={events}
          startAccessor="start"
          endAccessor="end"
          // Prevent any native "title" tooltip
          tooltipAccessor={() => undefined}
          selectable={false}
          onSelectEvent={onSelectEvent}
          step={15}
          timeslots={4}
          min={new Date(1970, 0, 1, 7, 0)}   // 07:00
          max={new Date(1970, 0, 1, 21, 0)}  // 21:00
          // Start roughly mid-day; tweak or compute from first event if you prefer
          scrollToTime={new Date(1970, 0, 1, 11, 0)}
          resources={employees}
          resourceIdAccessor={resourceIdAccessor}
          resourceTitleAccessor={resourceTitleAccessor}
          components={{
            event: EventContent,          // our compact, single-line content
            resourceHeader: ResourceHeader,
          }}
          culture={calendarCulture}
          messages={{
            today: t('calendarDay.calendar.today'),
            previous: t('calendarDay.calendar.previous'),
            next: t('calendarDay.calendar.next'),
            noEventsInRange: t('calendarDay.calendar.noEventsInRange'),
            showMore: (total) => t('calendarDay.calendar.showMore', { total }),
          }}
          style={{ background: '#fff', borderRadius: 8 }}
          // Colorize events by status: green for Wykonane, orange for Niewykonane
          eventPropGetter={(event) => {
            const s = event.status === 'Wykonane' ? 'Wykonane' : 'Niewykonane';
            const style = {};
            if (s === 'Wykonane') {
              style.backgroundColor = '#198754';
              style.borderColor = '#146c43';
            } else {
              style.backgroundColor = '#fd7e14';
              style.borderColor = '#e8590c';
            }
            style.padding = '2px 4px'; // tighter padding so content fits nicely
            return { style };
          }}
        />
      </div>

      {/* --- Edit dialog --- */}
      {editing && (
        <div className="card mt-3" ref={editCardRef}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <strong>{t('calendarDay.edit.title', { id: editing.id })}</strong>
              <span
                className={`badge ${ (editing?.status === 'Wykonane') ? 'bg-success' : 'bg-secondary' }`}
                title={t('calendarDay.edit.currentStatusTitle')}
              >
                {editing?.status === 'Wykonane' ? t('status.performed') : t('status.notPerformed')}
              </span>
            </div>
            <button className="btn btn-sm btn-outline-secondary" onClick={closeEdit}>{t('calendarDay.actions.close')}</button>
          </div>
          <div className="card-body">
            <form onSubmit={submitEdit} className="row g-3">
              <div className="col-12 col-md-3">
                <label className="form-label">{t('calendarDay.fields.date')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div className="col-12 col-md-2">
                <label className="form-label">{t('calendarDay.fields.time')}</label>
                <input
                  type="time"
                  className="form-control"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  required
                />
              </div>

              {/* Duration with presets + hours/minutes */}
              <div className="col-12 col-md-3">
                <label className="form-label">{t('calendarDay.fields.duration')}</label>

                {/* Preset buttons */}
                <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-label={t('calendarDay.edit.quickDurationPresetsAria')}>
                  {DURATION_PRESETS.map(({ H, M }) => {
                    const isActive = form.durationHours === H && form.durationMinutes === M;
                    const label = `${H}:${String(M).padStart(2, '0')}`;
                    return (
                      <button
                        key={`${H}-${M}`}
                        type="button"
                        className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm({ ...form, durationHours: H, durationMinutes: M })}
                        aria-pressed={isActive}
                        title={t('calendarDay.edit.setDurationTitle', { duration: label })}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Hours + minutes inputs */}
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="12"
                    step="1"
                    className="form-control"
                    style={{ maxWidth: 90 }}
                    value={form.durationHours}
                    onChange={(e) => setForm({ ...form, durationHours: Number(e.target.value) })}
                    aria-label={t('calendarDay.fields.durationHoursAria')}
                    required
                  />
                  <span>h</span>
                  <select
                    className="form-select"
                    style={{ maxWidth: 110 }}
                    value={form.durationMinutes}
                    onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
                    aria-label={t('calendarDay.fields.durationMinutesAria')}
                  >
                    {[0, 15, 30, 45].map(m => (
                      <option key={m} value={m}>{String(m).padStart(2,'0')}</option>
                    ))}
                  </select>
                  <span>min</span>
                </div>

                <div className="form-text">{t('calendarDay.edit.durationHelp')}</div>
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label">{t('calendarDay.fields.employee')}</label>
                <select
                  className="form-select"
                  value={form.employee_id ?? ''}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                >
                  <option value="">{t('calendarDay.fields.unassigned')}</option>
                  {employees.map(emp => (
                    <option value={emp.id} key={emp.id}>{emp.title}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-2">
                <label className="form-label">{t('calendarDay.fields.status')}</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  required
                >
                  <option value="Wykonane">{t('status.performed')}</option>
                  <option value="Niewykonane">{t('status.notPerformed')}</option>
                </select>

                {/* Quick status buttons */}
                <div className="d-flex gap-2 mt-2" role="group" aria-label={t('calendarDay.edit.quickStatusAria')}>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.status === 'Wykonane' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setForm({ ...form, status: 'Wykonane' })}
                    title={t('calendarDay.edit.setPerformedTitle')}
                  >
                    ✓ {t('status.performed')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${form.status === 'Niewykonane' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setForm({ ...form, status: 'Niewykonane' })}
                    title={t('calendarDay.edit.setNotPerformedTitle')}
                  >
                    ✗ {t('status.notPerformed')}
                  </button>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">{t('calendarDay.fields.comment')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.status_comment}
                  onChange={(e) => setForm({ ...form, status_comment: e.target.value })}
                  placeholder={t('calendarDay.fields.commentPlaceholder')}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label">{t('calendarDay.fields.serviceValue')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  value={form.service_value}
                  onChange={(e) => setForm({ ...form, service_value: e.target.value })}
                  placeholder={t('calendarDay.fields.serviceValuePlaceholder')}
                />
              </div>

              <div className="col-12 d-flex gap-2">
                <button type="submit" className="btn btn-primary">{t('calendarDay.actions.saveChanges')}</button>
                <button type="button" className="btn btn-outline-secondary" onClick={closeEdit}>
                  {t('calendarDay.actions.cancel')}
                </button>
              </div>
            </form>

            {/* Read-only extra */}
            <div className="mt-3 text-muted" style={{ fontSize: '.9rem' }}>
              {editing?.extendedProps?.service && <span>{t('calendarDay.fields.service')}: <strong>{editing.extendedProps.service}</strong> • </span>}
              {editing?.extendedProps?.client && <span>{t('calendarDay.fields.client')}: <strong>{editing.extendedProps.client}</strong></span>}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-3 alert alert-info py-2">{t('common:common.loading')}</div>
      )}
    </div>
  );
};

export default RBCResourceDay;




